/**
 * wayai conversations — list conversations or show conversation detail
 *
 * Usage:
 *   wayai conversations                                       # list conversations
 *   wayai conversations <conversation_id>                     # show detail + messages
 *   wayai conversations <conversation_id> observability       # list per-message LLM traces
 *   wayai conversations <conversation_id> observability --message-id <id>
 *   wayai conversations --status ended                        # filter by status
 *   wayai conversations --status team --period 7d             # status + a time window
 *   wayai conversations --period 7d                           # analytics-powered list
 *   wayai conversations --from 2026-01-01 --to 2026-01-31
 *   wayai conversations --org <org_id> --status team          # every hub in one org
 *   wayai conversations --all --status team                   # every hub the login can see
 *   wayai conversations --json                                # raw JSON output
 */

import { requireAuth } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient, ApiError } from '../lib/api-client.js';
import { parsePeriod, parseDateArg, wantsHelp, UUID_RE } from '../lib/utils.js';
import { truncate } from '../lib/eval-format.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import {
  listConversationsAcrossHubs,
  type CrossHubScope,
} from '../lib/cross-hub-conversations.js';
import { runConversationObservability } from './observability.js';
import { fromIso, toIso } from '@wayai/core';
import type { GetConversationsResponse } from '@wayai/core/contracts';

const KANBAN_COL_WIDTH = 30;
const HUB_COL_WIDTH = 24;

/**
 * Detect the cross-hub scope BEFORE `resolveActiveHubId` runs: that helper exits
 * when no workspace hub can be resolved, and a cross-hub listing enumerates its
 * hubs from the API instead — it needs neither a workspace nor a repo binding.
 * Returns null for the ordinary single-hub paths.
 *
 * Both `--org <uuid>` and `--org=<uuid>` are read here. The equals form matters:
 * the main parse loop skips any unrecognized `-`-prefixed token, so an `--org=`
 * this function did not see would leave the command silently listing the single
 * workspace hub under a flag the user gave to widen the scope.
 */
export function detectCrossHubScope(args: string[]): CrossHubScope | null {
  const all = args.includes('--all');
  const orgIdx = args.findIndex((a) => a === '--org' || a.startsWith('--org='));
  const inlineOrg = orgIdx !== -1 && args[orgIdx].startsWith('--org=');
  const orgValue = orgIdx === -1
    ? undefined
    : (inlineOrg ? args[orgIdx].slice('--org='.length) : args[orgIdx + 1]);

  if (orgIdx !== -1) {
    if (!orgValue || orgValue.startsWith('-')) {
      console.error('--org requires an organization id (uuid). Use --all to list every organization.');
      process.exit(1);
    }
    if (!UUID_RE.test(orgValue)) {
      console.error(`Invalid --org value: ${orgValue}. Expected an organization id (uuid).`);
      process.exit(1);
    }
  }

  if (all && orgIdx !== -1) {
    console.error('--all and --org are mutually exclusive: --all already spans every organization.');
    process.exit(1);
  }
  if ((all || orgIdx !== -1) && args.includes('--hub')) {
    console.error('--hub cannot be combined with --all/--org: a cross-hub listing spans every accessible hub.');
    process.exit(1);
  }

  if (all) return { mode: 'all' };
  if (orgIdx !== -1) return { mode: 'org', orgId: orgValue as string };
  return null;
}

/**
 * Command-specific help. `conversations` is in `OWN_HELP_COMMANDS` (lib/utils.ts) so
 * `wayai conversations --help` falls through to here instead of printing the
 * global command list — without this, the `observability` subcommand is
 * undiscoverable via `--help` and agents guess at invented syntax.
 */
function printConversationsHelp(): void {
  console.error(`
wayai conversations — list conversations or inspect what an agent received

Usage:
  wayai conversations                                  List conversations
  wayai conversations --status <agent|team|ended>      Filter by status
  wayai conversations --status team --period 7d        Status + a last-activity window
  wayai conversations --period 7d                      Analytics-powered list
  wayai conversations --from <date> --to <date>        List within a date range
  wayai conversations --org <organization_id>          Every hub in one organization
  wayai conversations --all                            Every hub this login can access
  wayai conversations <conversation_id>                Show detail + messages
  wayai conversations <conversation_id> observability   List the LLM turns (per-message id, latency, tool_calls)
  wayai conversations <conversation_id> observability --message-id <id>
  wayai conversations <conversation_id> annotate --set key=value [--type numeric|categorical|text]
                                                       Set a post-hoc business outcome (e.g. customer_purchased=true)
                                                       on an ended conversation, as an analytics dimension. Repeat
                                                       --set for multiple keys. Needs the hub kept within its
                                                       conversation retention window.
                                                       Full record for one turn:
                                                       resolved system prompt, the exact
                                                       messages the model saw (rendered
                                                       additional_context, injected
                                                       timestamps, replayed history),
                                                       completion, tool calls, tokens

Options:
  --message-id <id>   Expand one turn (only with the \`observability\` subcommand)
  --limit <n>         Max rows (list view; PER HUB with --all/--org)
  --offset <n>        Skip rows (list view; per hub with --all/--org)
  --json              Raw JSON output

Time windows:
  With \`--status\`, or with \`--all\`/\`--org\`, the listing comes from the live hub
  index and \`--period\`/\`--from\`/\`--to\` bound LAST ACTIVITY. So
  \`--status team --to 2026-08-22\` is "team conversations with nothing since the
  22nd" — the idle queue. Without \`--status\` AND without \`--all\`/\`--org\`, a bare
  \`--period\`/\`--from\`/\`--to\` still runs the ClickHouse-backed analytics list,
  which windows conversation START and only ever holds ended conversations.

Cross-hub:
  \`--all\` / \`--org <id>\` list across the hubs \`wayai list\` enumerates PLUS the
  production hubs those name as their parents. Two different cases end in a hub
  going unread, and they are reported differently:
    - A production PARENT this login cannot read is NAMED as skipped and does not
      make the run fail — it is a standing property of the login, not an outage.
    - A production hub whose PREVIEW this login cannot see is not enumerable
      anywhere, so it is not covered and NOT reported at all. Reach it with
      \`--hub <id>\`.
  Each hub is read with the caller's own grants; a hub IN SCOPE that refuses is
  reported and the listing exits non-zero as partial rather than looking like a
  smaller queue, and an empty scope is reported as such, never as an empty queue.
  \`--org\` accepts \`--org <id>\` and \`--org=<id>\`.

The default text list omits message ids — use \`--json\` or the \`observability\`
subcommand to discover them. \`observability\` is the answer to "the agent did X —
what did it ACTUALLY see?".
`.trim());
}

export async function conversationsCommand(args: string[]): Promise<void> {
  if (wantsHelp(args)) {
    printConversationsHelp();
    return;
  }

  // Scope first: a cross-hub listing enumerates hubs from the API, so it must not
  // go through `resolveActiveHubId`/`requireRepoConfig` (both exit when the cwd is
  // not a bound workspace — a condition irrelevant to "show me every queue").
  const crossHubScope = detectCrossHubScope(args);
  const hubId = crossHubScope ? '' : resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  if (!crossHubScope) requireRepoConfig();

  // Parse args
  let status: 'agent' | 'team' | 'ended' | undefined;
  let conversationId: string | undefined;
  let subcommand: string | undefined;
  let messageId: string | undefined;
  let limit: number | undefined;
  let offset: number | undefined;
  let period: string | undefined;
  let from: string | undefined;
  let to: string | undefined;
  let jsonOutput = false;
  const annotations: Array<{ key: string; value: string }> = [];
  let annotationType: 'numeric' | 'categorical' | 'text' | undefined;

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--status') {
      const val = args[++i];
      if (val === 'agent' || val === 'team' || val === 'ended') {
        status = val;
      } else {
        console.error(`Invalid status: ${val}. Must be 'agent', 'team', or 'ended'.`);
        process.exit(1);
      }
    } else if (arg === '--limit') {
      limit = parseInt(args[++i], 10);
    } else if (arg === '--offset') {
      offset = parseInt(args[++i], 10);
    } else if (arg === '--message-id') {
      if (i + 1 >= args.length) { console.error('--message-id requires a value'); process.exit(1); }
      messageId = args[++i];
    } else if (arg === '--period') {
      if (i + 1 >= args.length) { console.error('--period requires a value'); process.exit(1); }
      period = args[++i];
    } else if (arg === '--from') {
      if (i + 1 >= args.length) { console.error('--from requires a value'); process.exit(1); }
      from = args[++i];
    } else if (arg === '--to') {
      if (i + 1 >= args.length) { console.error('--to requires a value'); process.exit(1); }
      to = args[++i];
    } else if (arg === '--set') {
      if (i + 1 >= args.length) { console.error('--set requires key=value'); process.exit(1); }
      const pair = args[++i];
      const eq = pair.indexOf('=');
      if (eq <= 0) { console.error(`--set must be key=value (got: ${pair})`); process.exit(1); }
      annotations.push({ key: pair.slice(0, eq), value: pair.slice(eq + 1) });
    } else if (arg === '--type') {
      const val = args[++i];
      if (val === 'numeric' || val === 'categorical' || val === 'text') {
        annotationType = val;
      } else {
        console.error(`Invalid --type: ${val}. Must be 'numeric', 'categorical', or 'text'.`);
        process.exit(1);
      }
    } else if (arg === '--json') {
      jsonOutput = true;
    } else if (arg === '--all' || arg.startsWith('--org=')) {
      // Scope flags — already read (and validated) by detectCrossHubScope.
    } else if (arg === '--org') {
      // Ditto; skip its value so a bare org uuid is never read as a conversation id.
      i++;
    } else if (!arg.startsWith('-')) {
      // Position 1: conversation_id. Position 2: subcommand (e.g. `observability`).
      // If the user puts the subcommand first ("wayai conversations observability"),
      // promote it to subcommand directly — that yields a clearer "missing
      // conversation_id" error instead of pretending `observability` is a UUID.
      if (conversationId === undefined && arg === 'observability') {
        subcommand = arg;
      } else if (conversationId === undefined) {
        conversationId = arg;
      } else if (subcommand === undefined) {
        subcommand = arg;
      } else {
        console.error(`Unexpected positional argument: ${arg}`);
        process.exit(1);
      }
    }
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  const hasAnalyticsFlags = !!(period || from || to);

  if (crossHubScope && (conversationId || subcommand)) {
    // Detail/observability/annotate all address one conversation in one hub.
    console.error('--all/--org apply to the conversation LIST only. Drop them and pass --hub <id> to open one conversation.');
    process.exit(1);
  }

  if (subcommand === 'observability') {
    if (!conversationId) {
      console.error('Usage: wayai conversations <conversation_id> observability [--message-id X] [--json]');
      process.exit(1);
    }
    try {
      await runConversationObservability(client, hubId, conversationId, { messageId, jsonOutput });
    } catch (err) {
      // Raw ApiError text isn't user-friendly at the CLI surface.
      if (err instanceof ApiError && err.status === 404) {
        console.error('Observability data not found. Either the conversation has no LLM turns yet, or R2 entries were purged.');
        process.exit(1);
      }
      // A retryable cold-start (CLICKHOUSE_COLD) that outlived the ApiClient's
      // bounded retry is transient, not a crash — surface it as such (and it's
      // already kept out of Sentry via ApiError.isRetryable; gh #2883).
      if (err instanceof ApiError && err.isRetryable) {
        console.error('ClickHouse is warming up (cold start); observability is temporarily unavailable. Retry in a few seconds.');
        process.exit(1);
      }
      throw err;
    }
    return;
  }

  if (subcommand === 'annotate') {
    if (!conversationId) {
      console.error('Usage: wayai conversations <conversation_id> annotate --set key=value [--type numeric|categorical|text]');
      process.exit(1);
    }
    if (annotations.length === 0) {
      console.error('annotate requires at least one --set key=value');
      process.exit(1);
    }
    const results: Array<{ key: string; type: 'numeric' | 'categorical' | 'text' }> = [];
    for (const { key, value } of annotations) {
      // Infer type when --type is omitted: numeric if the value parses as a finite
      // number, else categorical. The backend re-validates and coerces per type.
      const inferred: 'numeric' | 'categorical' =
        value.trim() !== '' && Number.isFinite(Number(value)) ? 'numeric' : 'categorical';
      const type = annotationType ?? inferred;
      await client.annotateConversation(hubId, conversationId, { key, value, type });
      results.push({ key, type });
    }
    if (jsonOutput) {
      console.log(JSON.stringify({ conversation_id: conversationId, annotations: results }, null, 2));
    } else {
      for (const r of results) console.log(`✓ Set annotation ${r.key} (${r.type})`);
    }
    return;
  }

  if (subcommand) {
    console.error(`Unknown conversations subcommand: ${subcommand}`);
    process.exit(1);
  }

  // `--message-id` is only meaningful with the `observability` subcommand.
  // Reject it elsewhere so a user typo (e.g. `wayai conversations <id> --message-id X`,
  // forgetting the subcommand) doesn't silently drop the flag.
  if (messageId !== undefined) {
    console.error('--message-id is only valid with the `observability` subcommand. Did you mean: wayai conversations <id> observability --message-id ' + messageId + '?');
    process.exit(1);
  }

  if (conversationId) {
    const [result, schedules] = await Promise.all([
      client.getConversation(hubId, conversationId, { limit, offset }),
      client.getPendingSchedules(hubId, conversationId).catch(() => ({ entries: [], total: 0 })),
    ]);

    if (jsonOutput) {
      console.log(JSON.stringify({ ...result, pending_schedules: schedules }, null, 2));
      return;
    }

    console.log(`Conversation: ${result.conversation.conversation_id}`);
    console.log(`Status: ${result.conversation.conversation_status}`);
    console.log(`Kanban: ${result.conversation.kanban_status ?? '(none)'}`);
    if (result.conversation.outcome) {
      console.log(`Outcome: ${result.conversation.outcome}`);
    }
    const user = result.conversation.user_name || result.conversation.user_email;
    if (user) {
      console.log(`User: ${user}`);
    }
    console.log(`Messages: ${result.total_messages}`);
    console.log('');

    for (const msg of result.messages) {
      const time = new Date(msg.created_at).toLocaleString();
      const meta = (msg.metadata ?? {}) as { tool_name?: unknown; arguments?: unknown; result?: unknown; is_error?: unknown };
      const toolName = typeof meta.tool_name === 'string' ? meta.tool_name : undefined;
      const isToolCall = msg.sender_type === 'agent' && msg.receiver_type === 'tool' && toolName;
      const isToolResult = msg.sender_type === 'tool' && msg.receiver_type === 'agent' && toolName;

      if (isToolCall) {
        console.log(`[tool_call: ${toolName}] (${time})`);
        if (meta.arguments !== undefined) {
          console.log(JSON.stringify(meta.arguments, null, 2));
        }
      } else if (isToolResult) {
        const label = meta.is_error === true ? 'tool_error' : 'tool_result';
        console.log(`[${label}: ${toolName}] (${time})`);
        if (meta.result !== undefined) {
          console.log(typeof meta.result === 'string' ? meta.result : JSON.stringify(meta.result, null, 2));
        } else if (msg.message_text) {
          console.log(msg.message_text);
        }
      } else if (msg.sender_type === 'system') {
        console.log(`[system → ${msg.receiver_type ?? '-'}] (${time})`);
        console.log(msg.message_text || '(no text)');
      } else {
        const from = msg.sender_description || msg.sender_type;
        const to = msg.receiver_description || msg.receiver_type;
        const routing = to ? `${from} → ${to}` : from;
        console.log(`[${routing}] (${time})`);
        console.log(msg.message_text || '(no text)');
      }
      console.log('');
    }

    // Show pending scheduled actions
    if (schedules.entries.length > 0) {
      console.log(`Pending Scheduled Actions (${schedules.entries.length}):`);
      for (const entry of schedules.entries) {
        const type = entry.schedule_type;
        const time = new Date(entry.run_at).toLocaleString();
        const order = entry.followup_order ? `#${entry.followup_order}` : '';
        const preview = entry.message_text.length > 50
          ? entry.message_text.slice(0, 47) + '...'
          : entry.message_text;
        console.log(`  ${type} ${order} — ${time}`);
        console.log(`    "${preview}"`);
      }
      console.log('');
    }

    // Where this sits in the same hub user's archived history. Printed as bare
    // ids, NOT as ready-to-run commands: this endpoint needs a live
    // ConversationDO, which is deleted after `conversation_retention_days` (<=30)
    // while the index keeps archived rows for up to `ended_index_retention_days`
    // (<=730) — so an older neighbour is a real conversation the CLI can no
    // longer open. Text mode only — the --json path returned above.
    const prevId = result.conversation.previous_conversation_id;
    const nextId = result.conversation.next_conversation_id;
    if (prevId || nextId) {
      if (prevId) console.log(`Previous conversation: ${prevId}`);
      if (nextId) console.log(`Next conversation:     ${nextId}`);
      console.log('(Neighbours by closing time. Ones past the conversation retention window are listed but can no longer be opened here.)');
      console.log('');
    }

    // Point-of-need nudge: an agent diagnosing "why did the agent say X" reads
    // this transcript and stops here — but the transcript is the *rendered*
    // output, not the model's input. Surface the observability drill-in at the
    // moment of diagnosis instead of relying on skill docs the session may not
    // have loaded. Text mode only — the --json path returned above.
    console.log('Diagnosing agent behavior? The transcript shows what was sent — not what the model received.');
    console.log(`Drill in:  wayai conversations ${conversationId} observability   (per-turn resolved prompt, tool calls, latency)`);
  } else if (crossHubScope) {
    // Cross-hub queue view. Reads the live hub index per hub (the only store that
    // holds OPEN `agent`/`team` conversations at all), so `--status` and the time
    // window both apply — see resolveActivityWindow for the axis.
    const window = resolveActivityWindow(period, from, to);

    // The hub cap throws `expected()` — the top-level catch in index.ts prints it
    // and exits 1 without reporting a routine "narrow your scope" to Sentry.
    const result = await listConversationsAcrossHubs(client, crossHubScope, {
      status,
      limit,
      offset,
      activityFrom: window.start,
      activityTo: window.end,
    });

    // No arithmetic here on purpose: `scopes_total`/`scopes_failed`/`hubs_read` are
    // derived once, at the seam that owns the accounting. Two review rounds in a row
    // produced a count here that contradicted another count printed two lines away.
    if (jsonOutput) {
      console.log(JSON.stringify(result, null, 2));
      // A listing missing some hubs is degraded, not smaller — exit non-zero so a
      // monitoring script cannot read a partial queue as an empty one (gh #2452's
      // lesson). Derived parents outside the caller's grants are NOT that: they are
      // a permanent property of the token, so counting them here would make the
      // exit code useless for telling an outage from a normal run.
      if (result.partial || result.hubs_queried === 0) process.exit(1);
      return;
    }

    for (const failure of result.errors) {
      const where = failure.scope_kind === 'hub'
        ? `hub ${sanitizeTerminalText(failure.hub_name || failure.hub_id || '')} (${failure.hub_id})`
        : `organization ${failure.organization_id}`;
      console.error(`Could not read ${where}: ${sanitizeTerminalText(failure.error)}`);
    }

    if (result.hubs_queried === 0) {
      // Zero hubs in scope is NOT an empty queue: nothing was read, so nothing can
      // be concluded about it. Rendering "No conversations found" here is exactly
      // the silent-undercount this listing exists to refuse.
      console.error(
        `No hubs were in scope across ${result.organizations_queried} organization(s), so no queue was read. ` +
          '`wayai list` shows the hubs this login can enumerate; use `--hub <id>` for one hub outside that set.',
      );
      process.exit(1);
    }

    if (result.hubs_out_of_scope.length > 0) {
      // Stated, not silent — but not a failure: these are production parents this
      // login cannot read, which the help text routes to `--hub <id>`. The ids are
      // printed, not just counted: `--hub <id>` is useless without exactly the id
      // this line would otherwise withhold. Bounded by the 100-hub cap.
      const ids = result.hubs_out_of_scope.map(sanitizeTerminalText).join(', ');
      console.error(
        `Note: ${result.hubs_out_of_scope.length} production hub(s) are outside this login's grants ` +
          `and were not read: ${ids}. Reach one with --hub <id>.`,
      );
    }

    if (result.conversations.length === 0) {
      console.log(`No conversations found across ${result.hubs_read} hub(s).`);
    } else {
      console.log(`Conversations (${result.conversations.length} across ${result.hubs_read} hubs):\n`);
      for (const conv of result.conversations) {
        // Hub and contact names are org-authored free text from an org the operator
        // may not control — this is the first CLI view that renders a REMOTE hub's.
        const user = sanitizeTerminalText(conv.user_name || conv.user_email || 'unknown');
        const updated = conv.updated_at ? new Date(conv.updated_at).toLocaleString() : '-';
        const env = conv.hub_environment === 'production' ? 'prod' : 'prev';
        const hub = truncate(sanitizeTerminalText(conv.hub_name || conv.hub_id), HUB_COL_WIDTH);
        console.log(`  ${hub.padEnd(HUB_COL_WIDTH)}  ${env}  ${conv.conversation_id}  ${conv.conversation_status.padEnd(7)}  ${user.padEnd(20)}  ${updated}`);
      }
    }

    if (result.partial) {
      console.error(`\nPartial listing: ${result.scopes_failed} of ${result.scopes_total} scope(s) could not be read.`);
      process.exit(1);
    }
  } else if (status && hasAnalyticsFlags) {
    // `--status` + a time window. The ClickHouse analytics list below cannot serve
    // this: its rows are written at conversation CLOSE, so it holds no open
    // `agent`/`team` conversation to filter — the very rows a support-queue check
    // is about. The live hub index holds every status, so the window applies there
    // (on last activity — see resolveActivityWindow).
    const window = resolveActivityWindow(period, from, to);
    const result = await client.getConversations(hubId, {
      status,
      limit,
      offset,
      activityFrom: window.start,
      activityTo: window.end,
    });
    printIndexList(result, jsonOutput);
  } else if (hasAnalyticsFlags) {
    // Analytics-powered list
    if ((from && !to) || (!from && to)) {
      console.error('--from and --to must both be provided together.');
      process.exit(1);
    }

    let start: string;
    let end: string;

    try {
      if (from && to) {
        start = parseDateArg(from, 'from');
        end = parseDateArg(to, 'to');
        if (new Date(start).getTime() >= new Date(end).getTime()) {
          console.error('--from must be before --to.');
          process.exit(1);
        }
      } else {
        const parsed = parsePeriod(period || '7d');
        start = parsed.start;
        end = parsed.end;
      }
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }

    const result = await client.getAnalyticsConversations({
      hub_id: hubId,
      time_interval: { start, end },
      limit: limit ?? 50,
      offset: offset ?? 0,
    });

    if (jsonOutput) {
      // Emit the full payload (the `cold` flag is part of it) so machine
      // consumers can inspect it, then exit non-zero on cold so batch/automation
      // pipelines treat a degraded listing as retryable rather than as a
      // complete "no conversations" result (gh #2452).
      console.log(JSON.stringify(result, null, 2));
      if (result.cold) process.exit(1);
      return;
    }

    // A cold result is a transient ClickHouse cold-start, NOT an empty hub.
    // Surface it distinctly — silently printing "No conversations found." here
    // is what made dozens of real ended conversations look undiscoverable in
    // gh #2452.
    if (result.cold) {
      console.error(
        'ClickHouse is warming up (cold start); the ended-conversation list is temporarily unavailable. Retry in a few seconds.',
      );
      process.exit(1);
    }

    if (result.conversations.length === 0) {
      console.log('No conversations found.');
      return;
    }

    console.log(`Conversations (${result.conversations.length} of ${result.total}):\n`);

    for (const conv of result.conversations) {
      const participant = conv.participant_name || conv.participant_phone || 'unknown';
      const updated = conv.updated_at ? new Date(conv.updated_at).toLocaleString() : '-';
      const msgs = conv.message_count != null ? `${conv.message_count} msgs` : '';
      console.log(`  ${conv.conversation_id}  ${participant.padEnd(20)}  ${msgs.padEnd(10)}  ${updated}`);
    }
  } else {
    // Original list path (backward compat)
    const result = await client.getConversations(hubId, { status, limit, offset });
    printIndexList(result, jsonOutput);
  }
}

/** Render (or dump) a hub-index conversation listing. Shared by the plain and windowed paths. */
function printIndexList(result: GetConversationsResponse, jsonOutput: boolean): void {
  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  if (result.conversations.length === 0) {
    console.log('No conversations found.');
    return;
  }

  console.log(`Conversations (${result.conversations.length} of ${result.total}):\n`);

  for (const conv of result.conversations) {
    const user = conv.user_name || conv.user_email || 'unknown';
    const updated = conv.updated_at ? new Date(conv.updated_at).toLocaleString() : '-';
    const kanban = truncate(
      conv.outcome
        ? `${conv.kanban_status ?? '-'}/${conv.outcome}`
        : (conv.kanban_status ?? '-'),
      KANBAN_COL_WIDTH,
    );
    console.log(`  ${conv.conversation_id}  ${conv.conversation_status.padEnd(7)}  ${kanban.padEnd(KANBAN_COL_WIDTH)}  ${user.padEnd(20)}  ${updated}`);
  }
}

/**
 * Resolve `--period`/`--from`/`--to` into a LAST-ACTIVITY window for the hub-index
 * listings. Two deliberate differences from the analytics path:
 *
 * - The axis is last activity, not conversation start. It is what the index sorts
 *   by and prints as `updated_at`, and it is the only axis that can express the
 *   reported need ("team conversations idle since X" = `--to X` alone).
 * - `--from` and `--to` are independent. Requiring both, as the analytics path
 *   does, would make an open-ended idle query impossible to write.
 *
 * Both bounds are normalized to canonical ISO-8601 UTC before they leave here.
 * `parseDateArg` passes any parseable `YYYY-MM-DDT…` through UNCHANGED, and
 * `listConversationsQuery` guards these params with `z.string().datetime()`,
 * which rejects a zoneless `2026-08-01T09:00:00` and a `+03:00` offset alike —
 * so without this, a value the CLI's own error text advertises ("full ISO
 * datetime") comes back as a raw 400, and in cross-hub mode as one 400 per hub.
 */
function resolveActivityWindow(
  period: string | undefined,
  from: string | undefined,
  to: string | undefined,
): { start?: string; end?: string } {
  let start: string | undefined;
  let end: string | undefined;

  if (from || to) {
    try {
      start = from ? canonicalIso(parseDateArg(from, 'from')) : undefined;
      end = to ? canonicalIso(parseDateArg(to, 'to')) : undefined;
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
    // `>` not `>=`: an equal from/to pair is a single-instant window, which the
    // contract's own refine accepts. Rejecting it here only would make the CLI
    // and the API disagree about the same input.
    if (start && end && Date.parse(start) > Date.parse(end)) {
      console.error('--from must be before --to.');
      process.exit(1);
    }
    return { start, end };
  }

  if (period) {
    try {
      const parsed = parsePeriod(period);
      return { start: parsed.start, end: parsed.end };
    } catch (err) {
      console.error(err instanceof Error ? err.message : String(err));
      process.exit(1);
    }
  }

  return {};
}

/**
 * Canonical ISO-8601 UTC (ms precision, `Z`) for an instant `parseDateArg` already
 * validated — through `@wayai/core` `time.ts`, the repo's only timestamp seam. A
 * zoneless datetime is a local wall-clock time, as a user typing it in their shell
 * means it, so converting to UTC preserves the instant rather than reinterpreting it.
 */
function canonicalIso(value: string): string {
  return toIso(fromIso(value));
}
